import { FormikProps } from 'formik';
import { Input } from '@components/atoms/Input';
import { Button } from '@components/atoms/Button';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { PasswordRequirements } from '@components/molecules/PasswordRequirements';
import { Card } from '@components/atoms/Card';
import { useTranslation } from '@hooks/useTranslation';

export interface ForceResetPasswordFormProps {
  onBack?: () => void;
  formik: FormikProps<{
    email: string;
    newPassword: string;
    confirmPassword: string;
  }>;
  submitting?: boolean;
}

export const ForceResetPasswordForm: React.FC<ForceResetPasswordFormProps> = ({
  formik,
  onBack,
  submitting = false,
}) => {
  const tLogin = useTranslation('login');
  return (
    <div className="flex justify-center items-center h-screen">
      <Card className="w-full max-w-md mx-auto">
        <div className="flex flex-col gap-4">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {tLogin.setNewPasswordTitle}
          </h1>
          <p className="text-gray-500 dark:text-gray-400">
            {tLogin.setNewPasswordSubtitle}
          </p>
        </div>
        <form
          onSubmit={formik.handleSubmit}
          className="flex flex-col gap-4 mt-8">
          <FormGroup
            error={formik.touched.email ? formik.errors.email : undefined}
            label={tLogin.email}
            htmlFor="force-email">
            <Input
              name="email"
              type="email"
              id="force-email"
              autoComplete="email"
              onBlur={formik.handleBlur}
              value={formik.values.email}
              onChange={formik.handleChange}
              placeholder={tLogin.enterEmail}
              error={Boolean(formik.touched.email && formik.errors.email)}
            />
          </FormGroup>
          <FormGroup
            label={tLogin.newPasswordLabel}
            htmlFor="force-new-password"
            error={formik.touched.newPassword ? formik.errors.newPassword : undefined}>
            <Input
              id="force-new-password"
              name="newPassword"
              type="password"
              autoComplete="new-password"
              onBlur={formik.handleBlur}
              value={formik.values.newPassword}
              onChange={formik.handleChange}
              placeholder={tLogin.enterPassword}
              error={Boolean(
                formik.touched.newPassword && formik.errors.newPassword,
              )}
            />
          </FormGroup>
          <FormGroup
            label={tLogin.confirmPasswordLabel}
            htmlFor="force-confirm-password"
            error={
              formik.touched.confirmPassword ? formik.errors.confirmPassword : undefined
            }>
            <Input
              id="force-confirm-password"
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              onBlur={formik.handleBlur}
              value={formik.values.confirmPassword}
              onChange={formik.handleChange}
              placeholder={tLogin.confirmNewPasswordPlaceholder}
              error={Boolean(
                formik.touched.confirmPassword && formik.errors.confirmPassword,
              )}
            />
          </FormGroup>
          <PasswordRequirements value={formik.values.newPassword} />
          <div className="flex gap-2 mt-4">
            {onBack && (
              <Button type="button" variant="secondary" onClick={onBack}>
                {tLogin.back}
              </Button>
            )}
            <Button
              fullWidth
              type="submit"
              variant="primary"
              disabled={submitting}>
              {tLogin.saveNewPassword}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default ForceResetPasswordForm;
